/**
 * The invented world the onboarding design study runs against.
 *
 * **Nothing here is real, deliberately.** The registry directories below stand in for
 * BrokerCheck, NIPR and SEC ADV, and putting real advisors, CRD numbers or work addresses
 * into a git repository to make a mock-up look convincing is a poor trade — the same
 * decision the Team Portal study took. The *shapes* are real: a person CRD is seven digits
 * (97 % of the 33 766 migrated rows), a firm CRD six, an SEC number is `801-` plus six.
 *
 * The firm register and the wirehouse list are the exception and are real, because they are the
 * branch condition itself: a team's route is entered by whether FINRA lists its lead at a firm on
 * the wirehouse list (`WIREHOUSE_FIRM_IDS`), and `TEAM_FIRMS` — survey-api's own seed of the
 * register — names the team's firm. A mock that invented firms would be mocking the decision
 * under review. Their FINRA firm ids are FINRA's public ones; the people said to work there are
 * not.
 */

import { ADD_SURVEY_ROUTE, HOME_ROUTE, LOGIN_ROUTE, REGISTRATION_ROUTE } from '@/domains/auth';
import type { IRunState, StepCode } from './flow';

/** Where a screen's behaviour comes from, and whether it exists today. */
export type Provenance = 'shipped' | 'stubbed' | 'not-built';

export const PROVENANCE_LABEL: Record<Provenance, string> = {
  shipped: 'Shipped',
  stubbed: 'Stubbed provider',
  'not-built': 'Not built'
};

export const PROVENANCE_NOTE: Record<Provenance, string> = {
  shipped: 'survey-api answers this today, on the route and with the body shown.',
  stubbed:
    'The route exists; the registry behind it is NotImplementedFactProvider, which answers ' +
    'FactStatus.Error and only that. NIPR is still that stub, so every FSP registration ' +
    'lands unverified; BrokerCheck is real since block 3 and SEC ADV since block 4.',
  'not-built':
    'No route answers this. The roadmap names the track that would build it — this screen ' +
    'is the specification for it, not a record of it.'
};

/**
 * Whether the registry providers are pretended live.
 *
 * The single most important switch on the page. `stubbed` is **today**: BrokerCheck is real
 * since survey-api's block 3 and SEC ADV since block 4, while NIPR is still
 * `NotImplementedFactProvider` — so every FSP lookup answers `error` and its happy path is
 * unreachable in the running product, and FA's and RIA's are not. Those two therefore answer
 * the same in both positions (see `providerStubbed` in `flow.ts`). `live` is what block 9
 * buys. A reviewer who cannot flip between the two ends up judging one of them without
 * knowing which.
 */
export type ProviderMode = 'stubbed' | 'live';

/** Who the registrant turns out to be — the thing the survey catalogue keys on. A team is an
 *  `org` participant: survey-api's `RegistrationSurveys` registers TEAMS as one, as a firm. */
export type SubjectKind = 'person' | 'org';

export type JourneyCode =
  | 'entry'
  | 'fa'
  | 'fsp'
  | 'ria'
  | 'teams'
  | 'returning'
  | 'activation'
  | 'unentitled';

export interface IJourney {
  code: JourneyCode;
  /** The rail label. */
  label: string;
  /** Who is at the keyboard, in the words the question was asked in. */
  who: string;
  blurb: string;
  provenance: Provenance;
  /** Roadmap reference for what is missing, when something is. */
  roadmap?: string;
}

export const JOURNEYS: IJourney[] = [
  {
    code: 'entry',
    label: 'Front door',
    who: 'Anybody arriving at the portal',
    blurb:
      'The sign-in screen, the “Create an account” link under it, and the question behind ' +
      'that link: which registration is this? The product asks it at /registration and ' +
      'offers four — an advisor, an insurance professional, a firm and a team. The journeys ' +
      'below are what it hands you to — reachable directly here, so a branch can be tested ' +
      'without walking the door every time.',
    provenance: 'shipped'
  },
  {
    code: 'fa',
    label: 'Advisor · new',
    who: 'An individual advisor with a CRD',
    blurb:
      'Registers on the CRD, BrokerCheck or IAPD confirms the name, and the account lands on a ' +
      'catalogue offering the FA survey to start. Registration opens no response: starting ' +
      'it does.',
    provenance: 'shipped'
  },
  {
    code: 'fsp',
    label: 'Insurance professional · new',
    who: 'A financial security professional with an NPN or a licence',
    blurb:
      'Two anchors rather than one, because FSP-AC-001/002 say the registrant holds either ' +
      'or both. The CRD is deliberately not an anchor here (FSP-AC-003).',
    provenance: 'stubbed'
  },
  {
    code: 'ria',
    label: 'Firm · new',
    who: 'An RIA firm registering on its SEC number',
    blurb:
      'The participant is the FIRM, not a person. An SEC number ADV does not match is told, ' +
      'at every attempt, that only SEC-registered firms are eligible — with the RIA-vs-Teams ' +
      'criteria and “Register as a team instead”, which ends the run with nothing made.',
    provenance: 'shipped'
  },
  {
    code: 'teams',
    label: 'Team · new',
    who: 'A team, registering on its lead advisor’s CRD',
    blurb:
      'The lead advisor’s CRD first, then the route: whether the firm FINRA BrokerCheck or ' +
      'the SEC’s IAPD lists the lead at is on the wirehouse list, and SR-416’s questions of ' +
      'TEAM-RG-001 and TEAM-RG-004…006 — private wealth, a trust company, submitting for an ' +
      'RIA, the practice’s structure. An RIA firm is sent to its own registration, as a new run.',
    provenance: 'shipped'
  },
  {
    code: 'returning',
    label: 'Migrated · has a password',
    who: 'One of the 9 716 whose legacy password survived our policy',
    blurb:
      'Signs in, is forced to change the password, and then meets the one-time identifier ' +
      'confirmation — which records what the respondent says about the number and verifies ' +
      'nothing: their own word is not a corroboration (survey-api’s Q4).',
    provenance: 'shipped'
  },
  {
    code: 'activation',
    label: 'Migrated · no password',
    who: 'One of the 45 336 who have no password and no way to obtain one',
    blurb:
      'The activation campaign: a token that reaches somebody, a password set, then the same ' +
      'confirmation. Mail can leave now, but only the four auth mails do — the campaign is ' +
      'the widest-blast-radius gap on the page.',
    provenance: 'not-built',
    roadmap: 'Roadmap B3 · the campaign, on the mail transport block 5 shipped'
  },
  {
    code: 'unentitled',
    label: 'Migrated · nothing offered',
    who: 'One of the 10 586 whose catalogue is empty',
    blurb:
      'Signs in, is made to change the password like every other migrated account, and ' +
      'arrives at a catalogue that answers []: this participant never answered a ' +
      'questionnaire and holds no number a ranking is built on. The population cuts across ' +
      'the two journeys above — some hold a legacy password, most will arrive from a link — ' +
      'and what it has in common is the screen at the end. Its way out is the product’s ' +
      '“Add a survey”: the four registrations, made inside the account.',
    provenance: 'shipped',
    roadmap: 'Shipped but for one answer: “none of these describes me” lands nowhere'
  }
];

export function journey(code: JourneyCode): IJourney {
  const found = JOURNEYS.find((entry) => entry.code === code);
  if (!found) throw new Error(`unknown journey ${code}`);
  return found;
}

/* ===========================================================================
   What each screen is a mock-up of
   ======================================================================== */

/**
 * Whether the **product** has the screen this one draws — a different question from
 * `Provenance`, which asks what the server answers, and the two come apart both ways. A
 * screen can be `shipped` on the wire and `not-built` here — as registration's were until
 * `/registration` rendered them, and the identifier confirmation until the catalogue drew its
 * card; and a step the product does draw can still lead to a screen nobody there reaches,
 * because the registry in front of it is a stub.
 *
 * - `live` — the product has this screen, and the strip above it links there — `/portal/surveys/add`
 *   for a registration made inside an account, `/registration` for one without;
 * - `stubbed` — the product's flow has the step, but the registry that leads to this screen —
 *   NIPR — is still `NotImplementedFactProvider`, so no registrant is shown it;
 * - `not-built` — the product does not have it, and the strip names what is missing and the
 *   roadmap block that would build it.
 */
export type ScreenStatus = 'live' | 'stubbed' | 'not-built';

/** A `live` screen carries its real route; the other two carry what is missing instead. */
export type ScreenMark =
  | { status: 'live'; route: string }
  | { status: Exclude<ScreenStatus, 'live'>; missing: string };

export const SCREEN_STATUS_LABEL: Record<ScreenStatus, string> = {
  live: 'Mock-up of a live screen',
  stubbed: 'Mock-up — in the product this registry is still a stub',
  'not-built': 'Not built yet — this is a mock-up'
};

const live = (route: string): ScreenMark => ({ status: 'live', route });
const notBuilt = (missing: string): ScreenMark => ({ status: 'not-built', missing });
const stubbed = (missing: string): ScreenMark => ({ status: 'stubbed', missing });

/** Registration's screens: `/registration` draws every one of them from the step the run is
 *  on — the chooser, the four steps, the redirect and the two ends of a run that are not an
 *  account — and `/portal/surveys/add` draws the same ones for a registration inside an account. */
const REGISTRATION = live(REGISTRATION_ROUTE);
const ADD_SURVEY = live(ADD_SURVEY_ROUTE);

/** The password screens the study still draws, retired from the product with passwords: it signs
 *  in through Invent's IdP. */
const PASSWORD_RETIRED = notBuilt(
  'Retired: the product signs in through Invent’s IdP (SR-476), and somebody who cannot sign in ' +
    'asks for an invitation at /sign-in/request.'
);

const ACTIVATION_UNSENT = notBuilt(
  'Nothing sends this: survey-api mails the four auth mails and nothing else, and the campaign ' +
    'is deferred — these accounts recover a password themselves at /password/forgot (block 6).'
);

const ACTIVATION_UNREACHED = notBuilt(
  'Reached only from an activation message nothing sends: the campaign is deferred (block 6, ' +
    'B3), though the mail transport it would ride on shipped in block 5.'
);

const NIPR_STUB = stubbed(
  'NIPR is still a stub in survey-api (roadmap block 9, C2): every lookup answers error, so no ' +
    'insurance registrant reaches a screen showing a match or a not-found.'
);

/** What the marks read off a run: the step, what the run has been through to reach it, and
 *  whether it is made inside an account. */
export type MarkedRun = Pick<IRunState, 'journey' | 'step' | 'lookup' | 'signedIn'>;

/**
 * One entry per step, so a step added to `StepCode` without a mark is a compile error rather
 * than a screen nobody labelled. The steps whose screen depends on the run take a function;
 * every other step is the same screen on every journey that reaches it.
 */
const SCREEN_MARKS: Record<StepCode, ScreenMark | ((run: MarkedRun) => ScreenMark)> = {
  // The product's `/login`, "Create an account" under the form included.
  sign_in_entry: live(LOGIN_ROUTE),
  choose_subject: registrationRoute,
  recover_request: PASSWORD_RETIRED,
  recover_sent: PASSWORD_RETIRED,
  recover_redeem: PASSWORD_RETIRED,
  anchor: registrationMark,
  // Reached on an `ok`, and by a number we already hold whatever the registry said — the only
  // way a run on `error` gets here, since a new registrant's goes on without a prompt.
  confirm_identity: registrationMark,
  // A team's alone, and its lookup is BrokerCheck's, which no switch position stubs.
  route: registrationRoute,
  complete: registrationMark,
  redirected: registrationRoute,
  // The two ends of a run that are not an account: neither shows anything the registry said,
  // and the product reaches both on the stub's `error` as on any other answer.
  refused: registrationRoute,
  run_closed: registrationRoute,
  sign_in: live(LOGIN_ROUTE),
  activation_notice: ACTIVATION_UNSENT,
  // Only the activation journey arrives here; the same screen reached with a password is
  // `forced_change`, which is the product's.
  set_password: ACTIVATION_UNREACHED,
  forced_change: PASSWORD_RETIRED,
  // The product asks it as a card on the catalogue, not as a screen in front of it — survey-api
  // ruled the question no gate — so the link goes to the catalogue, where the card is.
  confirm_identifier: live(HOME_ROUTE),
  landed: (run) => stubbedAnswer(run) ?? live(HOME_ROUTE),
  // The product's empty catalogue and its "Add a survey"; the one answer it lacks — "none of
  // these describes me" — is tagged in place.
  nothing_offered: live(HOME_ROUTE)
};

/** Which of the product's two routes draws a registration screen: the one inside an account,
 *  or the anonymous one. The mode is the run's, never the step's (the teams plan's R7). */
function registrationRoute(run: MarkedRun): ScreenMark {
  return run.signedIn ? ADD_SURVEY : REGISTRATION;
}

/**
 * A registration screen showing what a registry said, which carries what the run went through
 * to reach it: an FSP run holding an answer the stub never gives is on no product path.
 */
function registrationMark(run: MarkedRun): ScreenMark {
  return stubbedAnswer(run) ?? registrationRoute(run);
}

/**
 * NIPR is still `NotImplementedFactProvider`, which answers `error` and nothing else — so on
 * FSP every screen carrying an `ok` or a `not_found` is one no registrant reaches: the retry
 * message and the verified landing as much as the prompt. FA and RIA are the exceptions since
 * blocks 3 and 4, which is why they are absent here.
 */
function stubbedAnswer(run: MarkedRun): Nullable<ScreenMark> {
  if (run.lookup !== 'ok' && run.lookup !== 'not_found') return null;
  return run.journey === 'fsp' ? NIPR_STUB : null;
}

export function screenStatus(run: MarkedRun): ScreenMark {
  const mark = SCREEN_MARKS[run.step];
  return typeof mark === 'function' ? mark(run) : mark;
}

/* ===========================================================================
   The registries
   ======================================================================== */

/** What a registry lookup answered — `FactStatus` as the wire spells it. */
export type LookupResult = 'ok' | 'not_found' | 'error';

/** A firm FINRA lists a person at now — one of BrokerCheck's `currentEmployments[]` and
 *  `currentIAEmployments[]`, whose `firmId` and `firmName` are what survey-api reads. */
export interface IFinraFirm {
  finraFirmId: number;
  /** As FINRA writes it. */
  firmName: string;
}

/** One row of an invented registry. */
export interface IRegistryRow {
  /** `CRD`, `NPN`, `INSURANCE_LICENSE` or `SEC_NUMBER` — `IdentifierTypes` as the API spells them. */
  idType: string;
  value: string;
  /** What the registry would return as the legal name. */
  displayName: string;
  /** BrokerCheck's current firms for a person — none for a lead at no firm, whose team is asked
   *  about a trust company (TEAM-RG-001). */
  firms?: IFinraFirm[];
  /** The other number the same registration carries — NPN ⇄ licence (FSP-AC-001). */
  alsoKnownAs?: { idType: string; value: string };
}

export const REGISTRY: IRegistryRow[] = [
  // An invented firm, on no register and off the wirehouse list: a team led from it is asked
  // whether it submits for an RIA, under "Your lead advisor’s firm: …" in FINRA's own spelling.
  {
    idType: 'CRD',
    value: '7412356',
    displayName: 'Miriam A. Vance',
    firms: [{ finraFirmId: 990001, firmName: 'HARBOR RIDGE ADVISORS LLC' }]
  },
  // Morgan Stanley is on the wirehouse list, and its one FINRA firm is three register firms: the
  // private-wealth question, under FINRA's name, since no one register firm names the team.
  {
    idType: 'CRD',
    value: '6120884',
    displayName: 'Dennis Okafor',
    firms: [{ finraFirmId: 149777, firmName: 'MORGAN STANLEY' }]
  },
  // Wells Fargo's independent network, off the wirehouse list (Wells Fargo Clearing is on it):
  // "submitting for an RIA?", under the register's label.
  {
    idType: 'CRD',
    value: '5583017',
    displayName: 'Priya Raman',
    firms: [{ finraFirmId: 11025, firmName: 'WELLS FARGO ADVISORS FINANCIAL NETWORK, LLC' }]
  },
  // Rockefeller, on the wirehouse list: the private-wealth question, and Wires either way.
  {
    idType: 'CRD',
    value: '3367201',
    displayName: 'Sofia Ibarra',
    firms: [{ finraFirmId: 291361, firmName: 'ROCKEFELLER FINANCIAL LLC' }]
  },
  {
    idType: 'CRD',
    value: '4418872',
    displayName: 'Curtis Bellweather',
    firms: [{ finraFirmId: 990002, firmName: 'LAKESHORE FINANCIAL GROUP' }]
  },
  {
    idType: 'CRD',
    value: '2298431',
    displayName: 'Yolanda Priestly',
    firms: [{ finraFirmId: 990003, firmName: 'COPPER CREEK WEALTH' }]
  },
  // No current firm, so a team is asked about a trust company (TEAM-RG-001).
  { idType: 'CRD', value: '9004115', displayName: 'Theodore Nakamura' },
  {
    idType: 'CRD',
    value: '8830142',
    displayName: 'Harold Brantley',
    firms: [{ finraFirmId: 990004, firmName: 'STONEBRIDGE WEALTH GROUP' }]
  },
  // The two lead advisors a team already holds — see `HELD_IDENTIFIERS`.
  {
    idType: 'CRD',
    value: '2765190',
    displayName: 'Lena Marsh',
    firms: [{ finraFirmId: 6413, firmName: 'LPL FINANCIAL LLC' }]
  },
  {
    idType: 'CRD',
    value: '3902264',
    displayName: 'Owen Castellano',
    firms: [{ finraFirmId: 250, firmName: 'EDWARD D. JONES & CO., L.P.' }]
  },

  {
    idType: 'NPN',
    value: '70418822',
    displayName: 'Alicia Moreno',
    alsoKnownAs: { idType: 'INSURANCE_LICENSE', value: 'IL-4471902' }
  },
  {
    idType: 'INSURANCE_LICENSE',
    value: 'IL-4471902',
    displayName: 'Alicia Moreno',
    alsoKnownAs: { idType: 'NPN', value: '70418822' }
  },
  {
    idType: 'NPN',
    value: '70120577',
    displayName: 'Garrett Wilbourn',
    alsoKnownAs: { idType: 'INSURANCE_LICENSE', value: 'IL-2201884' }
  },
  // Both halves of every pair carry a row: FSP-AC-001 says either number finds the other, so
  // a licence whose NPN exists and which answers `not_found` itself is the fixture
  // contradicting the rule it is illustrating.
  {
    idType: 'INSURANCE_LICENSE',
    value: 'IL-2201884',
    displayName: 'Garrett Wilbourn',
    alsoKnownAs: { idType: 'NPN', value: '70120577' }
  },

  {
    idType: 'SEC_NUMBER',
    value: '801-114872',
    displayName: 'Cypress Grove Capital Management, LLC'
  },
  {
    idType: 'SEC_NUMBER',
    value: '801-220945',
    displayName: 'Ridgeline Fiduciary Partners, LLC'
  }
];

/**
 * Numbers our own `participant_identifier` already holds, and whether anything can log in
 * as that participant. The two cases are the difference between a refusal and a link:
 *
 * - `hasAccount: true` — the number is taken. The run ends, and the refusal names the
 *   **value**, never the holder (roadmap risk 4).
 * - `hasAccount: false` — one of the 258 the roadmap counted: a participant the rankings
 *   already know, with nobody able to act for it. The confirmation prompt names them, which
 *   is the one branch where naming somebody is the point.
 */
export interface IHeldIdentifier {
  idType: string;
  value: string;
  displayName: string;
  hasAccount: boolean;
  /** For the staff queue: what the ETL says about why there is no account. */
  origin?: string;
}

export const HELD_IDENTIFIERS: IHeldIdentifier[] = [
  {
    idType: 'CRD',
    value: '4418872',
    displayName: 'Curtis Bellweather',
    hasAccount: true
  },
  {
    idType: 'CRD',
    value: '2298431',
    displayName: 'Yolanda Priestly',
    hasAccount: false,
    origin: 'legacy login’s address was claimed by another row (54 of the 92)'
  },
  {
    idType: 'NPN',
    value: '70120577',
    displayName: 'Garrett Wilbourn',
    hasAccount: true
  },
  {
    idType: 'SEC_NUMBER',
    value: '801-220945',
    displayName: 'Ridgeline Fiduciary Partners, LLC',
    hasAccount: false,
    origin: 'firm participant whose lead advisor has no account to link to (26 of the 92)'
  },
  // A team holds its lead's CRD as `TEAM_LEAD_CRD`, and a team's registration is refused or
  // linked on that type alone: a person's own CRD of the same value is a different number.
  {
    idType: 'TEAM_LEAD_CRD',
    value: '2765190',
    displayName: 'Marsh Wealth Partners',
    hasAccount: true
  },
  {
    idType: 'TEAM_LEAD_CRD',
    value: '3902264',
    displayName: 'Castellano Private Client Group',
    hasAccount: false,
    origin: 'team participant whose lead advisor has no account to link to (26 of the 92)'
  }
];

/**
 * Addresses `account.email` already holds, and which of the two collisions each one is.
 *
 * The registrant is told one thing — `address_unavailable` — and the event distinguishes
 * them, because only one of the two has a way out the registrant can take.
 */
export type AddressCollision = 'duplicate_address' | 'address_belongs_to_another_account';

export const TAKEN_ADDRESSES: Record<string, AddressCollision> = {
  'curtis.bellweather@lakeshorefg.example': 'duplicate_address',
  'p.raman@wellsfargoadvisors.example': 'address_belongs_to_another_account'
};

/* ===========================================================================
   The firm register — survey-api's `TeamFirmSeed`, in part
   ======================================================================== */

/** A register firm. Its `route` column is survey-api's still, and routes nothing since SR-416 —
 *  the wirehouse list does — so the study does not carry it. */
export interface ITeamFirm {
  code: string;
  label: string;
  finraFirmIds: number[];
}

/**
 * The wirehouse list's FINRA firm ids — survey-api's `FirmListSeed.Wirehouse`, the client's 21
 * firms, copied as seeded. Staff own the list from then on (`/admin/firm-lists`), so the real one
 * may have moved (survey-api's `/admin/firm-lists`). A lead at any of them enters a team's route
 * at the private-wealth question.
 */
export const WIREHOUSE_FIRM_IDS: readonly number[] = [
  8158, 7059, 199, 250, 79, 463, 2347, 7691, 149777, 2908, 2881, 249, 46173, 705, 31194, 291361,
  793, 17499, 8174, 19616, 1252
];

/**
 * The register a team's firm is named from — seven of the forty firms survey-api's `TeamRouting`
 * migration seeds, copied as it seeds them: code, label and FINRA firm ids, in label order. Staff
 * own the register from then on (`/team/setup/team-routing`), so the real one may have moved; these are
 * the firms the invented leads above work at — one FINRA firm that is three register firms (no
 * one firm names the team), a firm with two FINRA ids, firms on and off the wirehouse list.
 */
export const TEAM_FIRMS: ITeamFirm[] = [
  { code: 'edward_jones', label: 'Edward Jones', finraFirmIds: [250] },
  {
    code: 'graystone',
    label: 'Graystone Consulting from Morgan Stanley',
    finraFirmIds: [149777]
  },
  { code: 'lpl', label: 'LPL', finraFirmIds: [6413] },
  {
    code: 'morgan_stanley_pwm',
    label: 'Morgan Stanley Private Wealth Management',
    finraFirmIds: [149777]
  },
  {
    code: 'morgan_stanley_wm',
    label: 'Morgan Stanley Wealth Management',
    finraFirmIds: [149777]
  },
  {
    code: 'rockefeller',
    label: 'Rockefeller Capital Management',
    finraFirmIds: [291361, 294197]
  },
  {
    code: 'wells_fargo_finet',
    label: 'Wells Fargo Advisors Financial Network',
    finraFirmIds: [11025]
  }
];

/* ===========================================================================
   Migrated accounts — the two populations §1.1 measured
   ======================================================================== */

export interface IMigratedAccount {
  email: string;
  /** The legacy password, for the journey that has one. Invented, like everything here. */
  password?: string;
  displayName: string;
  identifier: { idType: string; value: string };
  /** What BrokerCheck would say the firm is, for the confirmation step to show. */
  firmName: string;
  /** The survey their migrated responses sit in — what the scoped catalogue will offer. */
  surveyCode: string;
}

export const MIGRATED_ACCOUNTS: Record<'returning' | 'activation', IMigratedAccount> = {
  returning: {
    email: 'miriam.vance@harborridge.example',
    password: 'SpringHarbor2026',
    displayName: 'Miriam A. Vance',
    identifier: { idType: 'CRD', value: '7412356' },
    firmName: 'Harbor Ridge Advisors',
    surveyCode: 'FA'
  },
  activation: {
    email: 'harold.brantley@stonebridge.example',
    displayName: 'Harold Brantley',
    identifier: { idType: 'CRD', value: '8830142' },
    firmName: 'Stonebridge Wealth Group',
    surveyCode: 'FA'
  }
};

/**
 * The third migrated population, and the one nothing had been drawn for: an account whose
 * participant has never answered a questionnaire and holds no number a ranking anchors on.
 * Since SR-213 scoped the catalogue, `GET /my/cycles` answers `[]` for it.
 *
 * Deliberately **not** an `IMigratedAccount`. That shape exists to carry an anchoring number,
 * the firm behind it and the survey the responses sit in, and this account has none of the
 * three — inventing them to fit the record would hide the one fact the journey is about.
 */
export interface IUnentitledAccount {
  email: string;
  password: string;
  displayName: string;
  /** What the participant does hold. 13 967 of the 13 971 such participants carry this and
   *  an address and nothing else, and neither of those anchors anything. */
  legacyUserId: string;
}

export const UNENTITLED_ACCOUNT: IUnentitledAccount = {
  email: 'dana.whitfield@northpointadvisory.example',
  password: 'NorthpointHarbor2026',
  displayName: 'Dana Whitfield',
  legacyUserId: 'LEG-0042317'
};

/* ===========================================================================
   The scenario cheat-sheet
   ======================================================================== */

/**
 * One reachable outcome, and the value that reaches it.
 *
 * The flow is driven by what is typed rather than by a scenario switch, because a switch
 * that jumps straight to a refusal never shows the two screens before it. These are
 * shortcuts that fill the field — the branch is still taken by submitting it.
 */
export interface IScenario {
  journey: JourneyCode;
  label: string;
  /** What to put in the anchor field. */
  value: string;
  idType?: string;
  /** What it demonstrates. */
  outcome: string;
  /** Only reachable when the providers are pretended live. */
  needsLiveProvider?: boolean;
  /** The mirror of it: an outcome the live providers cannot produce. Listing the same
   *  number twice, once as ok and once as error, reads as a contradiction rather than as
   *  the switch it actually depends on. */
  needsStubbedProvider?: boolean;
}

export const SCENARIOS: IScenario[] = [
  {
    journey: 'fa',
    label: 'BrokerCheck matches',
    value: '7412356',
    outcome: 'ok → “Is this you?” → account and participant, and the catalogue offers FA'
  },
  {
    journey: 'fa',
    label: 'The number is taken',
    value: '4418872',
    outcome: 'Our own data holds it against an account → identifier_unavailable, run abandoned'
  },
  {
    journey: 'fa',
    label: 'A participant nobody can log in as',
    value: '2298431',
    outcome: 'One of the 258 → the prompt names them, and completion links rather than duplicates'
  },
  {
    journey: 'fa',
    label: 'No such CRD',
    value: '1000001',
    outcome: 'not_found → retry once, then carry on with no identifier at all'
  },
  {
    journey: 'fsp',
    label: 'NPN matches, licence pulled with it',
    value: '70418822',
    idType: 'NPN',
    outcome: 'FSP-AC-001: one lookup, both numbers',
    needsLiveProvider: true
  },
  {
    journey: 'fsp',
    label: 'Licence matches instead',
    value: 'IL-4471902',
    idType: 'INSURANCE_LICENSE',
    outcome: 'The second anchor, named by the caller rather than guessed at',
    needsLiveProvider: true
  },
  // The mirror of the two above, and what every FSP registration does today: NIPR is the stub,
  // so the number goes straight to the completion, whose notice says it stays unconfirmed.
  {
    journey: 'fsp',
    label: 'NIPR could not be asked',
    value: '70418822',
    idType: 'NPN',
    outcome: 'error → straight to the completion, the number stored unverified. Today’s branch.',
    needsStubbedProvider: true
  },
  {
    journey: 'ria',
    label: 'ADV matches the firm',
    value: '801-114872',
    outcome: 'The legal name from ADV Item 1.A, confirmed, then drawn read-only and sent as shown'
  },
  {
    journey: 'ria',
    label: 'No firm on that SEC number',
    value: '801-999999',
    outcome:
      'not_found at every attempt: the notice and the criteria → “Register as a team instead” ' +
      'ends the run with nothing made'
  },
  // R7 of survey-api's SEC ADV plan: the register holds exempt reporting advisers and matches
  // none of them, so the branch above is also where an ERA goes.
  {
    journey: 'ria',
    label: 'An exempt reporting adviser',
    value: '802-104221',
    outcome: 'The SEC holds it, but only registered advisers match → the notice, as above'
  },
  {
    journey: 'ria',
    label: 'Not an SEC number',
    value: '114872',
    outcome: '400 on the field before anything is asked — bare digits say neither 801 nor 802'
  },
  {
    journey: 'teams',
    label: 'A firm on the wirehouse list',
    value: '3367201',
    outcome: 'Rockefeller → “is this your lead advisor?” → the private-wealth question → Wires'
  },
  {
    journey: 'teams',
    label: 'One FINRA firm, three register firms',
    value: '6120884',
    outcome:
      'Morgan Stanley, three register firms → FINRA’s name above the private-wealth question'
  },
  {
    journey: 'teams',
    label: 'A firm off the list',
    value: '5583017',
    outcome: 'Wells Fargo’s network → submitting for an RIA? → the practice’s structure'
  },
  {
    journey: 'teams',
    label: 'A firm the register does not hold',
    value: '7412356',
    outcome: '“Your lead advisor’s firm: …” in FINRA’s spelling → submitting for an RIA?'
  },
  {
    journey: 'teams',
    label: 'No current firm',
    value: '9004115',
    outcome: 'No current firm → affiliated with a trust company?'
  },
  {
    journey: 'teams',
    label: 'No such CRD',
    value: '1000001',
    outcome: 'not_found twice → affiliated with a trust company?, and no number is stored'
  },
  {
    journey: 'teams',
    label: 'A lead another team registered',
    value: '2765190',
    outcome: 'Held as a team’s lead CRD with an account → identifier_unavailable, naming nobody'
  },
  {
    journey: 'teams',
    label: 'A team on file nobody signs in for',
    value: '3902264',
    outcome: 'The prompt names the team we hold → linked, and no question is asked'
  },
  {
    journey: 'fsp',
    label: 'The number is taken',
    value: '70120577',
    idType: 'NPN',
    outcome: 'Held against an account → identifier_unavailable, run abandoned'
  },
  {
    journey: 'ria',
    label: 'A firm nobody can log in as',
    value: '801-220945',
    outcome: 'The firm participant exists with no account → the completion links rather than duplicates'
  }
];

/**
 * Addresses worth typing at the completion step, and what each one refuses with.
 * Anything else is free.
 */
export const ADDRESS_HINTS: { email: string; outcome: string }[] = [
  {
    email: 'curtis.bellweather@lakeshorefg.example',
    outcome: 'Already an account — duplicate_address, and the run stays open to try another'
  },
  {
    email: 'p.raman@wellsfargoadvisors.example',
    outcome:
      'One of the 54: their participant exists and their address is somebody else’s account. ' +
      'Told exactly what a duplicate is told — only the run’s event records the difference — ' +
      'and if it is the only address they have, only staff can let them in, by a link on ' +
      'their participant card.'
  }
];

/**
 * How a refused registrant reaches a human — **and nobody has supplied it.**
 *
 * Three screens send somebody to "the Veritant research team" and none can say how: there is no
 * support address anywhere in this app and no contact route on survey-api, whose mail
 * transport sends four auth mails and receives nothing. It is client content nobody has
 * supplied, so it is `null` here rather than a plausible-looking address — an
 * invented `support@` is the one kind of placeholder that gets used for real by somebody who
 * does not know it was invented.
 */
export const SUPPORT_ADDRESS: Nullable<string> = null;

/** Min length and the blocklist, mirroring `PasswordPolicyOptions` (12 / 256 / common / local part). */
export const PASSWORD_MIN_LENGTH = 12;

export const COMMON_PASSWORDS = ['password1234', 'qwerty123456', '123456789012', 'letmein12345'];
